import React from "react";
import { SpotCat } from "@/hooks/useSpotCategories";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Clock, Trees, Star, Navigation } from "lucide-react";
import { HeartButton } from "@/components/HeartButton";
import { imgSrc } from "@/lib/img";
import { usePrefs } from "@/contexts/PrefsContext";
import type { Spot } from "@/lib/spots";

export const SpotCard = ({ s, index }: { s: Spot; index: number }) => {
  const { t, lang } = usePrefs();
  const name = lang === "en" && s.name_en ? s.name_en : s.name_ar;
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(index, 8) * 0.05 }}>
      <Link to={`/spots/${s.spot_id}`} data-testid={`spot-card-${s.spot_id}`} className="group block overflow-hidden rounded-[1.75rem] border bg-card hover:shadow-xl transition-shadow">
        <div className="relative aspect-[4/3] bg-emerald-100 dark:bg-emerald-950">
          {s.gallery[0] ? <img src={imgSrc(s.gallery[0])} alt={name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
            : <Trees className="absolute inset-0 m-auto h-14 w-14 text-emerald-600/60" />}
          <span className="absolute top-3 start-3 rounded-full bg-emerald-600 text-white text-xs font-bold px-3 py-1"><SpotCat slug={s.category} /></span>
          <div className="absolute top-3 end-3"><HeartButton spotId={s.spot_id} /></div>
        </div>
        <div className="p-4 space-y-1.5">
          <h3 className="font-display text-lg font-black leading-tight">{name}</h3>
          <p className="text-sm text-muted-foreground inline-flex items-center gap-1"><MapPin className="h-4 w-4" />{[s.governorate && t(`gov.${s.governorate}`), s.area].filter(Boolean).join(" · ")}</p>
          <div className="flex flex-wrap gap-3 text-xs font-bold">
            {!!s.rating_count && <span data-testid={`spot-rating-${s.spot_id}`} className="inline-flex items-center gap-1 text-amber-600"><Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" />{s.rating_avg} ({s.rating_count})</span>}
            {s.distance_km != null && <span data-testid={`spot-distance-${s.spot_id}`} className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400"><Navigation className="h-3.5 w-3.5" />{t("spot.km").replace("{n}", String(s.distance_km))}</span>}
          </div>
          {s.hours_ar && <p className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{s.hours_ar}</p>}
        </div>
      </Link>
    </motion.div>
  );
};
